<template>
  <v-container fluid>
    <v-layout wrap>
      <v-flex xs12 class="headline mb-2 ml-4"
        >Users <span class="caption">count: {{ users.length }}</span></v-flex
      >
      <v-col cols="12" xs="12" class="">
        <v-card>
          <v-tabs>
            <v-tab @click="active = true"
              >ACTIVE<span class="caption ml-1 grey--text"></span
            ></v-tab>
            <v-tab @click="active = false"
              >DISABLED<span class="caption ml-1 grey--text"></span
            ></v-tab>
          </v-tabs>
          <v-divider></v-divider>
          <v-data-table
            :headers="headers"
            :items="filteredUsers"
            :search="search"
            :loading="loading"
            loading-text="Loading... Please wait"
            sort-by="createDate"
            sort-desc
            class="elevation-1"
            dense
          >
            <template v-slot:top>
              <v-toolbar flat color="white" class="pt-1">
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  label="Filter by role or email"
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </v-toolbar>
            </template>
            <template v-slot:item.name="{ item }">
              <div style="display: flex; align-items: center; gap: 4px;">
                <span>{{ item.firstName }} {{ item.lastName }}</span>
                <v-btn icon x-small @click="openNameDialog(item)">
                  <v-icon x-small>edit</v-icon>
                </v-btn>
              </div>
              <div class="caption d-none">{{ item.id }}</div>
            </template>
            <template v-slot:item.role="{ item }">
              <div style="width:160px">
                <v-select
                  :items="[
                    'Foreman',
                    'Project Manager',
                    'Yard Manager',
                    'Office',
                    'Driver',
                    'Truck Manager',
                    'Mechanic',
                    'Equipment Manager',
                    'Payroll Clock-In',
                  ]"
                  v-model="item.role"
                  @change="updateRole(item)"
                  class="mt-0"
                  hide-details
                ></v-select>
              </div>
            </template>
            <template v-slot:item.foremanType="{ item }">
              <div style="width:180px" v-if="item.role === 'Foreman'">
                <v-select
                  :items="['Demolition', 'Core & Shell', 'Concrete & Steel', 'Asbestos']"
                  v-model="item.foremanType"
                  @change="updateForemanType(item)"
                  class="mt-0"
                  hide-details
                ></v-select>
              </div>
              <div style="width:180px" v-if="item.role === 'Driver'">
                <v-select
                  :items="['Packer Driver', 'Roll Off Driver', 'Outside Hauler']"
                  v-model="item.driverTypes"
                  @change="updateDriverTypes(item)"
                  class="mt-0"
                  hide-details
                  multiple
                  chips
                  small-chips
                  deletable-chips
                ></v-select>
              </div>
            </template>
            <template v-slot:item.email="{ item }">
              {{ item.email }}
            </template>
            <template v-slot:item.phone="{ item }">
              <a :href="`tel:${item.phone}`" style="text-decoration:none">{{
                item.phone
              }}</a>
            </template>
            <template v-slot:item.access="{ item }">
              <v-btn
                v-if="!item.access"
                color="success"
                small
                outlined
                @click="grantAccess(item.uid, true)"
                >Grant access</v-btn
              >
              <v-btn
                v-else
                color="error"
                small
                outlined
                @click="grantAccess(item.uid, false)"
                >Revoke Access</v-btn
              >
            </template>
            <template v-slot:item.permissionGroups="{ item }">
              <div style="display: flex; align-items: center; gap: 8px;">
                <v-btn icon small @click="openPermissionDialog(item)">
                  <v-icon small>edit</v-icon>
                </v-btn>
                <v-tooltip
                  bottom
                  v-if="item.permissionGroups && item.permissionGroups.length"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="info" v-bind="attrs" v-on="on">
                      info
                    </v-icon>
                  </template>
                  <span>{{ item.permissionGroups.join(", ") }}</span>
                </v-tooltip>
              </div>
            </template>
            <template v-slot:item.admin="{ item }">
              <v-icon v-if="item.isAdmin" color="green">check_circle</v-icon>
              <v-menu bottom left v-else :offset-y="true">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon color="grey">expand_more</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="makeAdmin(item)">
                    <v-list-item-title>Make Admin</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-flex xs12>
        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
          {{ snackText }}
          <v-btn text @click="snack = false">Close</v-btn>
        </v-snackbar>
      </v-flex>
    </v-layout>

    <!-- Edit Name Dialog -->
    <v-dialog v-model="nameDialog" max-width="420px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit Name</span>
        </v-card-title>
        <v-card-text>
          <div class="caption text--secondary mb-3">
            {{ selectedUser.email }}
          </div>
          <v-text-field
            v-model="editFirstName"
            label="First Name"
            :rules="[(v) => !!(v && v.trim()) || 'First name is required']"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="editLastName"
            label="Last Name"
            :rules="[(v) => !!(v && v.trim()) || 'Last name is required']"
            outlined
            dense
            hide-details="auto"
            @keyup.enter="nameIsValid && updateName()"
          ></v-text-field>
          <p class="caption text--secondary mt-4 mb-0">
            Note: this updates the user record only. Jobs, orders and reports
            that already have this person assigned keep the name as it was
            spelled when they were assigned.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="nameDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :disabled="!nameIsValid"
            @click="updateName"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Permission Groups Dialog -->
    <v-dialog v-model="permissionDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit Permission Groups</span>
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            <strong
              >{{ selectedUser.firstName }} {{ selectedUser.lastName }}</strong
            >
          </div>
          <v-chip-group
            v-model="selectedPermissions"
            column
            multiple
            active-class="primary--text"
          >
            <v-chip
              v-for="permission in availablePermissions"
              :key="permission"
              :value="permission"
              filter
              outlined
            >
              {{ permission }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="permissionDialog = false">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="updatePermissionGroups">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import fb from "@/main";
export default {
  data() {
    return {
      active: true,
      search: "",
      loading: true,
      users: [],
      headers: [
        {
          text: "Name",
          align: "left",
          sortable: false,
          value: "name",
        },
        { text: "Role", value: "role", width: 210 },
        { text: "Type", value: "foremanType", width: 180 },
        { text: "Email", value: "email" },
        { text: "Phone", value: "phone" },
        { text: "Access", value: "access" },
        { text: "Permissions", value: "permissionGroups", width: 140 },
        {
          text: "Super Admin  ",
          value: "admin",
          sortable: false,
          align: "center",
        },
      ],
      snack: false,
      snackColor: "",
      snackText: "",
      permissionDialog: false,
      nameDialog: false,
      editFirstName: "",
      editLastName: "",
      selectedUser: {},
      selectedPermissions: [],
      availablePermissions: [
        "allowManualPunch",
        "manageJobSites",
        "appSupport",
        "appSupportAdmin",
        "reports",
        "externalTruckRequests",
      ],
    };
  },
  async created() {
    try {
      this.unsubscribe = await fb.db
        .collection("users")
        .onSnapshot((querySnapshot) => {
          var users = [];
          querySnapshot.forEach(function(doc) {
            users.push({
              id: doc.id,
              ...doc.data(),
            });
          });
          this.users = users;
        });
      this.loading = false;
    } catch (error) {
      console.log(error);
    }
  },
  computed: {
    filteredUsers: function() {
      return this.users.filter((user) => {
        // Ensure 'access' property exists before comparison
        if (this.active) {
          return user.access === this.active;
        } else {
          return !user.hasOwnProperty("access") || user.access === this.active;
        }
      });
    },
    nameIsValid: function() {
      return !!(
        this.editFirstName &&
        this.editFirstName.trim() &&
        this.editLastName &&
        this.editLastName.trim()
      );
    },
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  methods: {
    grantAccess: async function(uid, type) {
      try {
        await fb.db
          .collection("users")
          .doc(uid)
          .update({
            access: type,
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Access successfully updated!";
      } catch (error) {
        console.log(error);
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
      }
    },
    async updateRole(user) {
      try {
        await fb.db
          .collection("users")
          .doc(user.id)
          .update({
            role: user.role,
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Role successfully updated!";
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
        console.log(error.message);
      }
    },
    async updateForemanType(user) {
      try {
        await fb.db
          .collection("users")
          .doc(user.id)
          .update({
            foremanType: user.foremanType,
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Foreman type successfully updated!";
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
        console.log(error.message);
      }
    },
    async updateDriverTypes(user) {
      try {
        await fb.db
          .collection("users")
          .doc(user.id)
          .update({
            driverTypes: user.driverTypes || [],
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Driver types successfully updated!";
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
        console.log(error.message);
      }
    },
    async makeAdmin(user) {
      try {
        await fb.db
          .collection("users")
          .doc(user.id)
          .update({
            isAdmin: true,
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "User successfully updated!";
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
        console.log(error.message);
      }
    },
    openNameDialog(user) {
      this.selectedUser = user;
      this.editFirstName = user.firstName || "";
      this.editLastName = user.lastName || "";
      this.nameDialog = true;
    },
    async updateName() {
      if (!this.nameIsValid) return;
      try {
        await fb.db
          .collection("users")
          .doc(this.selectedUser.id)
          .update({
            firstName: this.editFirstName.trim(),
            lastName: this.editLastName.trim(),
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Name successfully updated!";
        this.nameDialog = false;
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
        console.log(error.message);
      }
    },
    openPermissionDialog(user) {
      this.selectedUser = user;
      this.selectedPermissions = user.permissionGroups || [];
      this.permissionDialog = true;
    },
    async updatePermissionGroups() {
      try {
        await fb.db
          .collection("users")
          .doc(this.selectedUser.id)
          .update({
            permissionGroups: this.selectedPermissions,
          });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Permission groups successfully updated!";
        this.permissionDialog = false;
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error: ${error.message}`;
        console.log(error.message);
      }
    },
  },
};
</script>
